import { useEffect, useRef, useState } from 'react';
import {trackServiceLevelOutcome} from '../analytics';
import { usePodeVerDetalhe } from '../ServiceLevelContext';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Download, RefreshCw, Search, X } from 'lucide-react';
import {
  HOJE, getPedidosParaExport, getRegioes, getSellerPedidos, getSellerResumo, SellerResumo,
} from '../api';
import { getPrefs } from '../auth';
import KpiCard from '../components/KpiCard';
import Dica from '../components/Dica';
import { AJUDA } from '../utils/ajudas';
import TrendChart from '../components/TrendChart';
import Pagination from '../components/Pagination';
import { PedidoRow, Problema, Regiao } from '../types';
import {
  downloadCsv, fmtDataHora, fmtDataHoraFoto, fmtDias, fmtMoney, fmtMoney2, fmtNum, fmtPct, fmtPp,
} from '../utils/format';

const PAGE_SIZE = 20;
const MES_INICIO = HOJE.slice(0, 8) + '01';

interface Filtros {
  problema: Problema | '';
  de: string;
  ate: string;
  regiao_id: string;
  busca: string;
}

const FILTROS_INICIAIS: Filtros = { problema: '', de: MES_INICIO, ate: HOJE, regiao_id: '', busca: '' };

function FlagBadges({ p }: { p: PedidoRow }) {
  return (
    <>
      {p.flag_p1 && <span className="badge p1">P1 · {fmtDias(p.horas_em_atraso)}</span>}{' '}
      {p.flag_p2 && <span className="badge p2">P2 · {fmtPct(p.pct_atendimento_qtd, 0)}</span>}{' '}
      {p.flag_p3 && <span className="badge p3">P3 · {fmtMoney2(p.valor_corte)}</span>}
    </>
  );
}

export default function SellerDetail() {
  const { id } = useParams();
  const sellerId = Number(id);
  const navigate = useNavigate();
  const voltar = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/service-level/dashboard');
  };
  const [params, setParams] = useSearchParams();
  const industriaId = params.get('industria') ? Number(params.get('industria')) : null;
  const prefs = getPrefs();
  const janelaRef = prefs.janela_ref || 'YTD';
  const customDe = prefs.custom_de;
  const customAte = prefs.custom_ate;

  const [resumo, setResumo] = useState<SellerResumo | null>(null);
  const [erro, setErro] = useState('');
  const [erroPedidos, setErroPedidos] = useState('');
  const [erroExportacao, setErroExportacao] = useState('');
  const [tentativa, setTentativa] = useState(0);
  const [tentativaPedidos, setTentativaPedidos] = useState(0);
  const [regioes, setRegioes] = useState<Regiao[]>([]);

  const [filtros, setFiltros] = useState<Filtros>(FILTROS_INICIAIS);
  const [page, setPage] = useState(1);
  const [pedidos, setPedidos] = useState<{ total: number; pedidos: PedidoRow[] }>({ total: 0, pedidos: [] });
  const [loadingPedidos, setLoadingPedidos] = useState(true);
  const [exportando, setExportando] = useState(false);
  const exportacaoAtual = useRef(0);
  const podeVerPedidos = usePodeVerDetalhe(3);
  const periodoInvalido = Boolean(filtros.de && filtros.ate && filtros.de > filtros.ate);
  const filtrosAtivos = filtros.problema !== '' || filtros.regiao_id !== '' || filtros.busca !== ''
    || filtros.de !== MES_INICIO || filtros.ate !== HOJE;

  useEffect(() => {
    let ativo = true;
    setResumo(null);
    setErro('');
    if (!Number.isInteger(sellerId) || sellerId <= 0) {
      setErro('Este seller não foi encontrado. Volte ao fechamento e selecione um seller.');
      return;
    }
    getSellerResumo(sellerId, janelaRef, { de: customDe, ate: customAte }, industriaId)
      .then((result) => { if (ativo) setResumo(result); })
      .catch((e: Error) => { if (ativo) setErro(e.message); });
    return () => { ativo = false; };
  }, [sellerId, industriaId, janelaRef, customDe, customAte, tentativa]);

  useEffect(() => {
    let ativo = true;
    getRegioes().then((result) => { if (ativo) setRegioes(result); }).catch(() => {});
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    exportacaoAtual.current += 1;
    setExportando(false);
    setErroExportacao('');
    return () => { exportacaoAtual.current += 1; };
  }, [sellerId, industriaId, filtros]);

  useEffect(() => {
    let ativo = true;
    setErroPedidos('');
    if (!podeVerPedidos || periodoInvalido || !Number.isInteger(sellerId) || sellerId <= 0) {
      setLoadingPedidos(false);
      return;
    }
    setLoadingPedidos(true);
    getSellerPedidos(sellerId, { ...filtros, industria_id: industriaId, page, page_size: PAGE_SIZE })
      .then((result) => { if (ativo) setPedidos(result); })
      .catch((e: Error) => { if (ativo) setErroPedidos(e.message); })
      .finally(() => { if (ativo) setLoadingPedidos(false); });
    return () => { ativo = false; };
  }, [sellerId, industriaId, filtros, page, podeVerPedidos, periodoInvalido, tentativaPedidos]);

  const setF = (patch: Partial<Filtros>) => { setFiltros((f) => ({ ...f, ...patch })); setPage(1); };
  const toggleProblema = (p: Problema) => setF({ problema: filtros.problema === p ? '' : p });

  // exportação respeitando os filtros ativos (SDD §7.2) — evidência para a conversa com o distribuidor
  const exportar = async () => {
    if (exportando || periodoInvalido) return;
    const solicitacao = ++exportacaoAtual.current;
    setExportando(true);
    setErroExportacao('');
    try {
      const rows = await getPedidosParaExport(sellerId, { ...filtros, industria_id: industriaId });
      if (solicitacao !== exportacaoAtual.current) return;
      downloadCsv(
        `pedidos_${resumo?.seller.nome_fantasia?.replace(/\s+/g, '_') || sellerId}_${filtros.de}_${filtros.ate}.csv`,
        ['Pedido', 'Data', 'Lojista', 'Região', 'Indústrias', 'Status', 'P1', 'Dias em atraso', 'P2', '% atendimento', 'P3', 'Corte (R$)', 'Valor pedido (R$)', 'SLA (dias)'],
        rows.map((p) => [
          p.pedido_id, String(p.data_pedido).slice(0, 10), p.lojista, p.regiao,
          (p.industrias_nomes ?? []).join(', '), p.status_atual,
          p.flag_p1 ? 'sim' : '', p.horas_em_atraso !== null ? (p.horas_em_atraso / 24).toFixed(1) : '',
          p.flag_p2 ? 'sim' : '', p.pct_atendimento_qtd !== null ? (p.pct_atendimento_qtd * 100).toFixed(1) : '',
          p.flag_p3 ? 'sim' : '', p.valor_corte.toFixed(2), p.valor_pedido.toFixed(2),
          p.sla_configurado ? p.sla_horas / 24 : `${p.sla_horas / 24} (default)`,
        ])
      );
      trackServiceLevelOutcome('seller_export', true, {seller_id: sellerId, rows: rows.length});
    } catch (e) {
      trackServiceLevelOutcome('seller_export', false, {seller_id: sellerId});
      if (solicitacao === exportacaoAtual.current) setErroExportacao(e instanceof Error ? e.message : 'Não foi possível exportar os pedidos. Tente novamente.');
    } finally {
      if (solicitacao === exportacaoAtual.current) setExportando(false);
    }
  };

  if (erro) return <div className="card state-panel" role="alert"><h2>Não foi possível carregar o seller</h2><p>{erro}</p><div className="filters"><button type="button" className="btn" onClick={() => setTentativa((n) => n + 1)}><RefreshCw size={16} /> Tentar novamente</button><Link className="btn secondary" to="/service-level/dashboard"><ArrowLeft size={16} /> Voltar ao fechamento</Link></div></div>;
  if (!resumo) return <div className="card state-panel" role="status" aria-live="polite"><p className="empty">Carregando indicadores do seller…</p></div>;

  const k = resumo.kpis;
  const pr = resumo.problemas;

  return (
    <>
      <nav className="breadcrumb-nav" aria-label="Navegação estrutural">
        <Link to="/service-level/dashboard">Fechamento</Link><ChevronRight size={14} aria-hidden="true" /><span aria-current="page">Seller</span>
      </nav>
      <div className="page-header">
        <div>
          <button type="button" className="btn secondary mb-2" onClick={voltar}><ArrowLeft size={14} /> Voltar</button>
          <div className="eyebrow">Desempenho do seller</div>
          <h1>{resumo.seller.nome_fantasia}</h1>
          <div className="sub">
            {resumo.seller.razao_social} · CNPJ {resumo.seller.cnpj} · {resumo.seller.uf_base}
          </div>
          {resumo.industria && (
            <div className="mt-2">
              <span className="seller-chip">
                Recorte: {resumo.industria.nome}
                <button type="button" onClick={() => { const next = new URLSearchParams(params); next.delete('industria'); setParams(next); }} aria-label="Limpar recorte de indústria"><X size={14} /></button>
              </span>
            </div>
          )}
        </div>
        <span className="foto-badge">Última atualização {fmtDataHoraFoto(resumo.ts_foto)}</span>
      </div>

      <div className="kpi-row">
        <KpiCard
          label="NS Quantidade (Fill Rate)" ajuda={AJUDA.ns}
          valores={{ hoje: k.hoje.ns_qtd, mtd: k.mtd.ns_qtd, m1: k.m1.ns_qtd, ref: k.ref.ns_qtd }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
          extra={`sobre pedidos já faturados (${fmtPct(k.mtd.pct_apurado)} do volume do mês)`}
        />
        <KpiCard
          label="Pedidos no Prazo" ajuda={AJUDA.prazo}
          valores={{ hoje: k.hoje.pct_no_prazo, mtd: k.mtd.pct_no_prazo, m1: k.m1.pct_no_prazo, ref: k.ref.pct_no_prazo }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
        />
        <KpiCard
          label="Corte" ajuda={AJUDA.corte}
          valores={{ hoje: k.hoje.pct_corte, mtd: k.mtd.pct_corte, m1: k.m1.pct_corte, ref: k.ref.pct_corte }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="down"
          extra={`${fmtMoney(k.mtd.corte_valor)} cortados no mês`}
        />
        <KpiCard
          label="Faturamento do mês" ajuda={AJUDA.apuracao}
          valores={{ hoje: k.hoje.pct_apurado ?? null, mtd: k.mtd.pct_apurado ?? null, m1: k.m1.pct_apurado ?? null, ref: k.ref.pct_apurado ?? null }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
          extra={`${fmtNum(k.mtd.pedidos_em_aberto)} pedidos em aberto, aguardando faturamento`}
        />
      </div>

      <div className="card">
        <h2 className="flex items-start justify-between gap-2"><span>NS de quantidade — tendência diária do mês</span><Dica titulo="Tendência diária" texto={AJUDA.tendencia} /></h2>
        <TrendChart serie={resumo.serie_ns} />
      </div>

      <div className="section-heading">
        <div><h2>Pontos de atenção</h2><p>Problemas identificados no mês{podeVerPedidos ? '. Selecione um para filtrar os pedidos abaixo.' : '.'}</p></div>
      </div>
      <div className="problema-row">
        {([
          { id: 'P1', titulo: 'Pedidos parados / em atraso', valor: fmtNum(pr.p1.pedidos), sub: `no mês · ${fmtNum(pr.p1.hoje)} enviados com atraso hoje` },
          { id: 'P2', titulo: 'Entrega parcial', valor: fmtNum(pr.p2.pedidos), sub: `no mês · ${fmtNum(pr.p2.hoje)} finalizados hoje` },
          { id: 'P3', titulo: 'Corte', valor: fmtMoney(pr.p3.valor), sub: `${fmtNum(pr.p3.pedidos)} pedidos no mês · ${fmtNum(pr.p3.hoje)} faturados com corte hoje` },
        ] as { id: Problema; titulo: string; valor: string; sub: string }[]).map((problema) => (
          <div key={problema.id} className={`problema ${filtros.problema === problema.id ? 'on' : ''}`}>
            <div className="flex items-start justify-between"><span className={`tag ${problema.id.toLowerCase()}`}>{problema.id}</span><Dica titulo={`${problema.id} · ${problema.titulo}`} texto={AJUDA.problemas} /></div>
            <button type="button" className="problem-filter" onClick={() => toggleProblema(problema.id)} aria-pressed={filtros.problema === problema.id} disabled={!podeVerPedidos} aria-label={`Filtrar pedidos: ${problema.titulo}`}>
              <span className="problem-title">{problema.titulo}</span>
              <span className="big">{problema.valor}</span>
              <span className="sub">{problema.sub}</span>
            </button>
          </div>
        ))}
      </div>

      {podeVerPedidos && (
      <div className="card">
        <div className="section-heading">
          <div><h2>Pedidos com problemas</h2><p>Consulte os detalhes e exporte os pedidos deste recorte.</p></div>
          <span className="badge perfil">{loadingPedidos ? 'Atualizando…' : `${fmtNum(pedidos.total)} pedidos`}</span>
        </div>
        <div className="filters detail-filter-grid">
          <label className="filter-field"><span>Problema</span>
            <select value={filtros.problema} onChange={(e) => setF({ problema: e.target.value as Problema | '' })}>
              <option value="">Todos os problemas</option><option value="P1">P1 · Parado / atrasado</option><option value="P2">P2 · Entrega parcial</option><option value="P3">P3 · Corte</option>
            </select>
          </label>
          <label className="filter-field"><span>Região</span>
            <select value={filtros.regiao_id} onChange={(e) => setF({ regiao_id: e.target.value })}>
              <option value="">Todas as regiões</option>{regioes.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </label>
          <label className="filter-field"><span>Data inicial</span><input type="date" value={filtros.de} max={filtros.ate || undefined} onChange={(e) => setF({ de: e.target.value })} aria-invalid={periodoInvalido} /></label>
          <label className="filter-field"><span>Data final</span><input type="date" value={filtros.ate} min={filtros.de || undefined} onChange={(e) => setF({ ate: e.target.value })} aria-invalid={periodoInvalido} /></label>
          <label className="filter-field filter-search"><span><Search size={13} aria-hidden="true" /> Buscar pedido ou lojista</span><input type="search" placeholder="Nº do pedido ou nome…" value={filtros.busca} onChange={(e) => setF({ busca: e.target.value })} /></label>
          <div className="filter-actions">
            <button type="button" className="btn secondary" onClick={() => { setFiltros(FILTROS_INICIAIS); setPage(1); }} disabled={!filtrosAtivos}><X size={16} /> Limpar filtros</button>
            <button type="button" className="btn" data-analytics-feature='Nível de Serviço · Exportar pedidos do seller' onClick={exportar} disabled={exportando || loadingPedidos || Boolean(erroPedidos) || periodoInvalido || pedidos.total === 0}><Download size={16} /> {exportando ? 'Exportando…' : `Exportar (${fmtNum(pedidos.total)})`}</button>
          </div>
        </div>
        {periodoInvalido && <div className="inline-alert" role="alert">A data final deve ser igual ou posterior à data inicial.</div>}
        {erroExportacao && <div className="inline-alert" role="alert">{erroExportacao}</div>}
        {erroPedidos && <div className="state-panel" role="alert"><p>{erroPedidos}</p><button type="button" className="btn secondary" onClick={() => setTentativaPedidos((n) => n + 1)}><RefreshCw size={16} /> Tentar novamente</button></div>}

        {loadingPedidos && <p className="empty" role="status" aria-live="polite">Carregando pedidos…</p>}

        {!loadingPedidos && !erroPedidos && !periodoInvalido && (
          <>
            <div className="desktop-only table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Pedido</th><th>Data</th><th>Lojista</th><th>Região</th><th>Status</th>
                    <th>Problemas</th><th className="num">Valor</th><th className="num">SLA</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidos.pedidos.map((p) => (
                    <tr key={p.pedido_id}>
                      <td><Link to={`/service-level/pedidos/${encodeURIComponent(p.pedido_id)}`}><strong>{p.pedido_id}</strong></Link></td>
                      <td>{fmtDataHora(p.data_pedido)}</td>
                      <td>{p.lojista}</td>
                      <td>{p.regiao}</td>
                      <td><span className={`badge ${p.status_atual}`}>{p.status_atual.replace('_', ' ')}</span></td>
                      <td><FlagBadges p={p} /></td>
                      <td className="num">{fmtMoney2(p.valor_pedido)}</td>
                      <td className="num">
                        {p.sla_configurado
                          ? `${p.sla_horas / 24}d`
                          : <span className="badge default-sla" title="SLA não configurado — usando default global">{p.sla_horas / 24}d default</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pedidos.total === 0 && <p className="empty">Nenhum pedido flagado com esses filtros.</p>}
            </div>

            {/* mobile: cards com tap para o detalhe (SDD §7.7) */}
            <div className="mobile-only">
              {pedidos.pedidos.map((p) => (
                <Link key={p.pedido_id} to={`/service-level/pedidos/${encodeURIComponent(p.pedido_id)}`} className="pedido-card">
                  <div className="top">
                    <strong>Pedido {p.pedido_id}</strong>
                    <span className={`badge ${p.status_atual}`}>{p.status_atual.replace('_', ' ')}</span>
                  </div>
                  <div className="mid"><strong>{p.lojista}</strong><br />{p.regiao} · {fmtDataHora(p.data_pedido)}</div>
                  <div className="mid">SLA: {p.sla_horas / 24} dias{!p.sla_configurado ? ' · padrão global' : ''}</div>
                  <div className="bot">
                    <FlagBadges p={p} />
                    <span className="ml-auto font-bold">{fmtMoney2(p.valor_pedido)}</span>
                  </div>
                </Link>
              ))}
              {pedidos.total === 0 && <p className="empty">Nenhum pedido flagado com esses filtros.</p>}
            </div>

            <Pagination page={page} pageSize={PAGE_SIZE} total={pedidos.total} onChange={setPage} />
          </>
        )}
      </div>
      )}
    </>
  );
}
